import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './VideoTrimmingControls.module.scss';
import { throttle } from 'engage/utils/general';

const DEFAULT_TOOLTIP_POSITION = -38;
const RIGHT_OFFSET = -38;

const FloatingContainer = ({
  parentRef,
  floatingValue,
  children,
  className,
  correction = 0,
  tooltip,
  disabled,
  minLeftOffset = null,
}) => {
  const myContainerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [leftOffset, setLeftOffset] = useState(correction);
  const [isHover, setIsHover] = useState(false);

  const calculateLeftOffset = useCallback(() => {
    const parentOffsetWidth = parentRef.current?.offsetWidth;
    const currentOffsetWidth = myContainerRef.current?.offsetWidth;

    if (!Number.isFinite(parentOffsetWidth) || !Number.isFinite(currentOffsetWidth)) {
      return;
    }

    const progress = floatingValue * parentOffsetWidth;
    if (progress + currentOffsetWidth > parentOffsetWidth) {
      setLeftOffset(progress - currentOffsetWidth - correction);

      return;
    }

    setLeftOffset(progress + correction);
  }, [floatingValue, myContainerRef, parentRef, setLeftOffset]);

  useEffect(() => {
    if (myContainerRef.current && parentRef.current) {
      const resizeContainer = new ResizeObserver(throttle(entries => {
        entries.forEach(() => {
          calculateLeftOffset();
        });
      }, 150));

      resizeContainer.observe(parentRef.current);

      return () => resizeContainer.disconnect();
    }
  }, [floatingValue, parentRef, myContainerRef]);

  useEffect(() => {
    if (tooltip?.length && tooltipRef.current) {
      if (isHover) {
        const BOTTOM_MENU_OFFSET = 10;
        const customStylesBottom = window
          .getComputedStyle(tooltipRef.current)
          .getPropertyValue('bottom')
          .match(/-?\d+/g)
          .map(Number)[0];
        let bottom = customStylesBottom;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        const { offsetHeight, offsetTop } = tooltipRef.current;
        const containerRect = tooltipRef.current.getBoundingClientRect();
        const positionY = containerRect.y - offsetTop + BOTTOM_MENU_OFFSET - customStylesBottom;
        if (positionY + offsetHeight > height) {
          bottom = customStylesBottom + offsetHeight * 2;
        } else if (positionY < 0) {
          bottom = customStylesBottom + positionY;
        }
        if (bottom !== DEFAULT_TOOLTIP_POSITION) {
          tooltipRef.current.style.bottom = `${bottom}px`;
          tooltipRef.current.style.zIndex = `21`;
        }
        if (minLeftOffset !== null) {
          const calculatedStylesRight = window
            .getComputedStyle(tooltipRef.current)
            .getPropertyValue('right')
            .match(/-?\d+/g)
            .map(Number)[0];
          if (tooltipRef.current.getBoundingClientRect().left < minLeftOffset) {
            tooltipRef.current.style.right = `${calculatedStylesRight -
              Math.abs(minLeftOffset - tooltipRef.current.getBoundingClientRect().left)}px`;
          }
        }
      } else {
        tooltipRef.current.style.bottom = `${DEFAULT_TOOLTIP_POSITION}px`;
        tooltipRef.current.style.right = `${RIGHT_OFFSET}px`;
        tooltipRef.current.style.zIndex = `20`;
      }
    }
  }, [isHover, tooltipRef.current]);

  return (
    <div
      ref={myContainerRef}
      className={cx(styles.floatingContainer, className)}
      style={{ left: `${leftOffset}px`, ...(tooltip && { zIndex: 56 }) }}
      onMouseOver={() => setIsHover(true)}
      onMouseOut={() => setIsHover(false)}
    >
      {children}
      {!disabled && tooltip && (
        <div ref={tooltipRef} className={cx(styles.tooltip, { [styles.visible]: isHover })}>
          {tooltip}
        </div>
      )}
    </div>
  );
};

export default FloatingContainer;
